// Archivo: api.js
// Este m�dulo contiene las utilidades para realizar peticiones HTTP (usando fetch)
// e incluye la l�gica de intercepci�n para manejar la expiraci�n del Access Token
// utilizando el Refresh Token de forma autom�tica (silenciosa).

// =========================================================================
// GESTI�N DEL ESTADO DEL REFRESH TOKEN
// =========================================================================

// Bandera para evitar que m�ltiples llamadas 401 disparen m�ltiples procesos de refresh
let isRefreshing = false;
// Cola de peticiones fallidas que esperan un nuevo token
let failedQueue = [];

// URL base de los usuarios para el endpoint de refresco.
// Aseg�rate de que esta ruta coincida con la configuraci�n de tu aplicaci�n.
const urlUsers = '/api/Usuarios';
const urlRefresh = `${urlUsers}/refresh`;

/**
 * Procesa la cola de promesas fallidas, resolvi�ndolas o rechaz�ndolas.
 * @param {Error | null} error - Error si el refresh fall�.
 */
const processQueue = (error) => {
  // eslint-disable-next-line no-console
  console.log(`[Auth] Procesando cola. Error: ${error ? 'S�' : 'No'}. Tama�o: ${failedQueue.length}`);
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      // Resolver sin argumentos. Esto le indica a handleTokenRefreshAndRetry
      // que puede re-ejecutar la llamada original.
      prom.resolve();
    }
  });
  failedQueue = [];
};

// =========================================================================
// L�GICA DE ALMACENAMIENTO Y AUTENTICACI�N
// =========================================================================

/**
 * Devuelve los par�metros de autenticaci�n (headers) para la petici�n.
 */
const authParams = ({
  method,
  contentType,
  data = null,
}) => {
  // Nota: Asumimos que la acci�n de login guarda 'token' (Access Token) y
  // 'refreshToken' (Refresh Token) en localStorage.
  const currentUser = JSON.parse(localStorage.getItem('currentUser')) || {};
  const params = {
    method,
  };

  params.headers = {};

  // Si existe el token, a�adir el header de autorizaci�n
  if (currentUser.token) {
    params.headers.Authorization = `Bearer ${currentUser.token}`;
  }

  if (!contentType && data) {
    params.body = data;
    params.headers.redirect = 'follow';
  }

  if (contentType === 'application/json' && data) {
    params.headers = {
      ...params.headers,
      'Content-Type': contentType,
    };

    params.body = JSON.stringify(data);
  }

  // Para peticiones de subida de archivos (FormData), no se necesita Content-Type
  if (contentType) {
    params.headers['Content-Type'] = contentType;
  }

  return params;
};

/**
 * Llama al endpoint de Refresh Token para obtener un nuevo par de tokens.
 * @param {string} expiredToken - El Access Token expirado.
 * @param {string} refreshToken - El Refresh Token actual.
 * @returns {Promise<string>} - El nuevo Access Token.
 */
const getNewTokens = async (expiredToken, refreshToken) => {
  // console.log(`[Auth] Iniciando llamada a Refresh Token: ${urlRefresh}`);
  const data = {
    accessToken: expiredToken, // Se env�a el token expirado
    refreshToken,
  };

  // Usamos 'fetch' directamente para evitar que esta petici�n sea interceptada
  const response = await fetch(urlRefresh, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (response.ok) {
    const authData = await response.json();
    const newAccessToken = authData.token;
    const newRefreshToken = authData.refreshToken;

    if (!newAccessToken) {
      // console.error(
      //   `[Auth] ERROR: Refresh Token exitoso,
      //   pero el servidor no devolvi� un Access Token v�lido (campo "Token" ausente o nulo).`,
      // );
      throw new Error('Refresh token succeeded but no new Access Token was received.');
    }

    // Almacenar el nuevo par de tokens (Access Token y Refresh Token)
    const currentUserData = JSON.parse(localStorage.getItem('currentUser')) || {};

    const loginData = {
      ...currentUserData, // Mantener datos de usuario existentes
      ...authData,
      isAuthenticated: true,
      token: newAccessToken,
      refreshToken: newRefreshToken,
    };
    localStorage.setItem('currentUser', JSON.stringify(loginData));

    // const newToken = newAccessToken.substring(newAccessToken.length - 8);
    // console.log(
    //   `[Auth] Refresh Token exitoso. Nuevo Access Token guardado (termina en ${newToken})`,
    // );

    return newAccessToken; // Devolver el nuevo Access Token
  }

  // Si falla (ej. 401 o 400), forzamos el logout.
  // console.error(`[Auth] Fallo en la llamada a Refresh Token. Status: ${response.status}`);
  throw new Error('Refresh token failed. Logging out.');
};

// =========================================================================
// FUNCI�N CLAVE: MANEJAR 401, REFRESH Y REINTENTO
// =========================================================================

/**
 * Maneja el error 401, intenta refrescar el token y reintenta la petici�n original.
 * @param {Function} apiCall - La funci�n que ejecuta la petici�n fetch original (e.g., apiGet, apiPost).
 * @param {Array} args - Los argumentos para la funci�n de petici�n original.
 * @param {Function} checkFunction - La funci�n de chequeo de resultados (checkResults, checkDownloadResults, etc.)
 * @returns {Promise<any>} - El resultado de la petici�n reintentada.
 */
const handleTokenRefreshAndRetry = (apiCall, args, checkFunction) => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser'));
  const refreshToken = currentUser ? currentUser.refreshToken : null;
  const expiredToken = currentUser ? currentUser.token : null;

  // console.warn(
  //   `[Auth] Petici�n fallida con 401. Token expirado:
  //     ${expiredToken ? expiredToken.substring(expiredToken.length - 8) : 'N/A'}`,
  // );

  // 1. Si no hay Refresh Token, forzar el logout inmediatamente
  if (!refreshToken) {
    // console.error('[Auth] ERROR: No hay Refresh Token, forzando logout.');
    localStorage.removeItem('currentUser');
    window.location.href = '/';
    // CORRECCI�N: Rechazar con una instancia de Error
    return Promise.reject(new Error('No refresh token available. Forced logout.'));
  }

  // 2. Devolver una promesa que se resolver�/rechazar� cuando el refresh termine.
  const retryPromise = new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });

  // 3. Si no hay un proceso de refresh activo, iniciarlo.
  if (!isRefreshing) {
    isRefreshing = true;

    getNewTokens(expiredToken, refreshToken)
      .then(() => {
        // �xito: Procesar la cola, lo que dispara el reintento de las promesas.
        processQueue(null);
      })
      .catch((error) => {
        // Fallo: Forzar logout y rechazar todas las promesas en cola
        // console.error('[Auth] Fallo cr�tico del Refresh Token. Forzando logout.');
        localStorage.removeItem('currentUser');
        window.location.href = '/';
        processQueue(error);
      })
      .finally(() => {
        isRefreshing = false;
      });
  }

  // 4. Esperar a que el token se refresque, y luego reintentar la llamada.
  return retryPromise.then(() => apiCall(...args).then(response => checkFunction(response, apiCall, args)));
};

// =========================================================================
// FUNCIONES DE CHEQUEO DE RESULTADOS (MODIFICADAS)
// =========================================================================

// Funciones de chequeo para peticiones normales (JSON)
const checkResults = (result, apiCall, args) => {
  if (result.ok) {
    return result.status === 204 ? Promise.resolve(result) : result.json();
  }

  // --- L�GICA DE INTERCEPCI�N DEL 401 ---
  if (result.status === 401) {
    // console.error(`[Auth] Reintento fallido con 401: ${args[0]}`);
    // En lugar de salir, intentar refrescar y reintentar
    return handleTokenRefreshAndRetry(apiCall, args, checkResults);
  }

  // --- L�GICA DE RATE LIMITING (429) ---
  if (result.status === 429) {
    const retryAfter = result.headers.get('Retry-After');
    const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : 60;

    // eslint-disable-next-line no-console
    console.warn(`[RateLimit] Request bloqueado por rate limiting. Reintentando en ${retrySeconds} segundos...`);

    // Crear una promesa que espere el tiempo indicado y luego reintente
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // eslint-disable-next-line no-console
        console.log(`[RateLimit] Reintentando request despu�s de ${retrySeconds} segundos...`);
        apiCall(...args)
          .then(response => checkResults(response, apiCall, args))
          .then(resolve)
          .catch(reject);
      }, retrySeconds * 1000);
    });
  }

  if (result.status === 400) {
    return result.json().then((error) => {
      const ex = {
        ...error,
        status: result.status,
      };
      throw ex;
    });
  }

  // Para cualquier otro error (5xx, etc.)
  throw result;
};

// Funciones de chequeo para peticiones de descarga (Blob)
const checkDownloadResults = (result, apiCall, args) => {
  if (result.ok) {
    return result.blob();
  }

  // --- L�GICA DE INTERCEPCI�N DEL 401 ---
  if (result.status === 401) {
    // console.error(`[Auth] Reintento fallido con 401: ${args[0]}`);
    // En lugar de salir, intentar refrescar y reintentar
    return handleTokenRefreshAndRetry(apiCall, args, checkDownloadResults);
  }

  // --- L�GICA DE RATE LIMITING (429) ---
  if (result.status === 429) {
    const retryAfter = result.headers.get('Retry-After');
    const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : 60;

    // eslint-disable-next-line no-console
    console.warn(`[RateLimit] Download bloqueado por rate limiting. Reintentando en ${retrySeconds} segundos...`);

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // eslint-disable-next-line no-console
        console.log(`[RateLimit] Reintentando download despu�s de ${retrySeconds} segundos...`);
        apiCall(...args)
          .then(response => checkDownloadResults(response, apiCall, args))
          .then(resolve)
          .catch(reject);
      }, retrySeconds * 1000);
    });
  }

  if (result.status === 400) {
    return result.json().then((error) => {
      throw error;
    });
  }

  throw result;
};

// Funciones de chequeo para peticiones de descarga de fotos (Blob + FileName)
const checkDownloadPhotoResults = async (response, apiCall, args) => {
  if (response.ok) {
    // Obtener el nombre del archivo del header Content-Disposition
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = 'archivo_descargado';

    if (contentDisposition) {
      const match = contentDisposition.match(/filename\*?=['"]?(?:UTF-8'')?([^;\n"']+)/);
      if (match) {
        fileName = decodeURIComponent(match[1]);
      }
    }
    return { fileName, file: await response.blob() };
  }

  // --- L�GICA DE INTERCEPCI�N DEL 401 ---
  if (response.status === 401) {
    // console.error(`[Auth] Reintento fallido con 401: ${args[0]}`);
    // En lugar de salir, intentar refrescar y reintentar
    return handleTokenRefreshAndRetry(apiCall, args, checkDownloadPhotoResults);
  }

  // --- L�GICA DE RATE LIMITING (429) ---
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After');
    const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : 60;

    // eslint-disable-next-line no-console
    console.warn(`[RateLimit] Photo download bloqueado por rate limiting. Reintentando en ${retrySeconds} segundos...`);

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // eslint-disable-next-line no-console
        console.log(`[RateLimit] Reintentando photo download despu�s de ${retrySeconds} segundos...`);
        apiCall(...args)
          .then(resp => checkDownloadPhotoResults(resp, apiCall, args))
          .then(resolve)
          .catch(reject);
      }, retrySeconds * 1000);
    });
  }

  if (response.status === 400) {
    return response.json().then((error) => {
      throw error;
    });
  }

  throw response;
};

// =========================================================================
// EXPORTACI�N DE FUNCIONES DE API (MODIFICADAS)
// =========================================================================
// Cada funci�n ahora encapsula su l�gica de 'fetch' en 'apiCall' y pasa
// 'apiCall' y 'args' a la funci�n de chequeo.

// --- GET ---
export const apiGet = (url) => {
  const args = [url];
  const apiCall = u => fetch(u, authParams({
    method: 'GET',
    contentType: 'application/json',
  }));
  return apiCall(...args).then(result => checkResults(result, apiCall, args));
};

export const apiDownload = (url) => {
  const args = [url];
  const apiCall = u => fetch(u, authParams({
    method: 'GET',
    contentType: 'application/json',
  }));
  return apiCall(...args).then(result => checkDownloadResults(result, apiCall, args));
};

export const apiDownloadPhoto = (url) => {
  const args = [url];
  const apiCall = u => fetch(u, authParams({
    method: 'GET',
    contentType: 'application/json',
  }));
  return apiCall(...args).then(result => checkDownloadPhotoResults(result, apiCall, args));
};

// --- POST ---
export const apiDownloadPost = (url, data) => {
  const args = [url, data];
  const apiCall = (u, d) => fetch(u, authParams({
    method: 'POST',
    contentType: 'application/json',
    data: d,
  }));
  return apiCall(...args).then(result => checkDownloadResults(result, apiCall, args));
};

export const apiUploadPost = (url, files) => {
  const args = [url, files];
  const apiCall = (u, f) => {
    const data = new FormData();
    [...f].forEach(file => data.append(file.name, file));

    // El Content-Type no se pasa en authParams para permitir que fetch lo maneje con FormData
    return fetch(u, authParams({
      method: 'POST',
      data,
      contentType: null, // Indicar a authParams que no a�ada Content-Type
    }));
  };
  return apiCall(...args).then(result => checkDownloadResults(result, apiCall, args));
};

export const apiPost = (url, data) => {
  const args = [url, data];
  const apiCall = (u, d) => fetch(u, authParams({
    method: 'POST',
    contentType: 'application/json',
    data: d,
  }));
  return apiCall(...args).then(result => checkResults(result, apiCall, args));
};

// --- PUT ---
export const apiPut = (url, id, data) => {
  const args = [url, id, data];
  const apiCall = (u, i, d) => fetch(`${u}/${i}`, authParams({
    method: 'PUT',
    contentType: 'application/json',
    data: d,
  }));
  return apiCall(...args).then(result => checkResults(result, apiCall, args));
};

// --- DELETE ---
export const apiDelete = (url) => {
  const args = [url];
  const apiCall = u => fetch(u, authParams({
    method: 'DELETE',
    contentType: 'application/json',
  }));
  return apiCall(...args).then(result => checkResults(result, apiCall, args));
};
